/*
 * Settings → Integrations → Webhooks — the wording and small rules the card uses, pure so they
 * can be checked (`node src/lib/webhooks.checks.mjs`). DESIGN-api-and-webhooks.md step 2,
 * 25/10/2026; mock-up `Claude outputs/api-mock-v1.html` sections 3–5.
 */

/* The boxes, in the order the server stores them (`services/webhooks.TICKS`). */
export const TICKS = ['requested', 'approved', 'declined', 'cancelled'];

export const SICKNESS_HINT =
  'Otherwise sickness shows as “Time off”. Sickness is health data, so only tick this if the system at this address needs it.';

/* The server checks properly (and again on every send); this only catches the obvious early. */
export function urlProblem(url) {
  const u = (url || '').trim();
  if (!u) return 'Enter the address to send to.';
  if (!/^https:\/\//i.test(u)) return 'The address must start https://';
  if (/\s/.test(u)) return 'That address has a space in it.';
  return null;
}

export function canAdd(url, ticks) {
  return !urlProblem(url) && (ticks || []).some((t) => TICKS.includes(t));
}

/* "hooks.zapier.com/…/abx3/" — the start and the end, so two Zapier hooks can be told apart. */
export function shortUrl(url) {
  const m = /^https:\/\/([^/?#]+)(\/[^?#]*)?/i.exec(url || '');
  if (!m) return url || '';
  const host = m[1].toLowerCase();
  const parts = (m[2] || '/').split('/').filter(Boolean);
  if (parts.length === 0) return host;
  if (parts.length <= 2) return `${host}/${parts.join('/')}`;
  return `${host}/…/${parts.slice(-2).join('/')}`;
}

/* The little picture beside a line of "What we sent". */
export const LINE_ICONS = {
  sent: { char: '✓', cls: 'bg-green-500/15 text-green-700 dark:text-green-400', label: 'Delivered' },
  waiting: { char: '↻', cls: 'bg-amber-500/15 text-amber-700', label: 'Trying again' },
  failed: { char: '✕', cls: 'bg-red-500/15 text-red-600', label: 'Not delivered' },
  paused: { char: '⏸', cls: 'bg-secondary text-muted-foreground', label: 'Held while paused' },
  test: { char: '✎', cls: 'bg-blue-500/15 text-blue-700', label: 'Test message' },
};

export function lineIcon(line) {
  if (line.event === 'test' && line.state === 'sent') return LINE_ICONS.test;
  return LINE_ICONS[line.state] || LINE_ICONS.waiting;
}

const pad = (n) => String(n).padStart(2, '0');

/* ["Today", "14:02"] or ["23/10", "09:15"] — 24-hour, UK order (CONVENTIONS.md). */
export function whenParts(iso, now = new Date()) {
  const d = new Date(iso || '');
  if (Number.isNaN(d.getTime())) return ['', ''];
  const time = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  const sameDay = d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth()
    && d.getDate() === now.getDate();
  if (sameDay) return ['Today', time];
  const y = new Date(now); y.setDate(now.getDate() - 1);
  const yesterday = d.getFullYear() === y.getFullYear() && d.getMonth() === y.getMonth()
    && d.getDate() === y.getDate();
  return [yesterday ? 'Yesterday' : `${pad(d.getDate())}/${pad(d.getMonth() + 1)}`, time];
}

/* The line under an address: "Approved, cancelled · last sent 14:02". */
export function endpointLine(ep, now = new Date()) {
  const bits = [ep.summary];
  if (ep.show_sickness) bits.push('sickness shown as sickness');
  if (ep.last_sent_at) {
    const [day, time] = whenParts(ep.last_sent_at, now);
    bits.push(`last sent ${day === 'Today' ? time : `${day} ${time}`}`);
  } else {
    bits.push('nothing delivered yet');
  }
  return bits.join(' · ');
}

/* working · failing · paused — the badge beside an address. */
export function health(ep) {
  if (ep.paused_at) return 'paused';
  if (ep.failing_since) return 'failing';
  return 'working';
}
